import {useEffect, useState} from "preact/compat";
import {api} from "../../../../../../services";
import {useAppContext} from "../../../../../../contexts";

export const useHomeDeliveryPicker = props => {
    // Destructured props
    const {
        attendanceId,
        handleSelectedValue,
        handleErrors,
        handleIsFreeFreight,
        handleCartLoader
    } = props;

    // State to store the input value, in this case the zip code
    const [formValues, setFormValues] = useState({
        zip: ""
    });
    // State to store the zip data after it has been fetched
    const [zipData, setZipData] = useState({
        address: "",
        value: "",
    });
    // State to store all possible errors
    const [errors, setErrors] = useState({
        isUnavailable: false
    })

    // Method to handle the input value change
    const handleChange = e => {
        // Destructured method props
        const {name, value} = e.target;

        // Formats the input value to the CEP standards
        const formattedValue = value.replace(/\D/g, '')
            .replace(/(\d{5})(\d)/, '$1-$2')
            .replace(/(-\d{3})\d+?$/, '$1')

        // Sets the formatted value to the "zip" state
        setFormValues(prevState => {
            return {
                ...prevState,
                [name]: formattedValue
            }
        });
    }

    // Method to handle the input blur action
    const handleBlur = () => {
        const zipRegex = /^[0-9]{5}-[0-9]{3}$/;

        // TODO: think of a better way to implement this validation logic
        // If there's already a zip address data set, then it does nothing
        // if (zipData.address) {
        //     return;
        // }

        // If regex isn't valid, then it resets the values if any has been already set
        if (!zipRegex.test(formValues.zip)) {
            // Resets the zipData state
            setZipData({
                address: "",
                value:   "",
            })

            // Resets the error state
            setErrors({
                isUnavailable: false,
            })

            // Resets the formatted data to be sent to the main controller
            const data = {
                id:      "",
                zip:     "",
                address: ""
            }

            // Assigns the formatted data to the main controller state
            handleSelectedValue({
                data
            })

            handleErrors(false);

            return;
        }

        // If validation is successful, then it
        fetchZip(formValues.zip).then(() => {});
    }

    // Method to actually fetch the zip code
    const fetchZip = async value => {
        try {
            // Activates the full page loader component
            handleCartLoader(true);

            // Performs the request to the backend
            let response = await api.get(`/shipping/cep/get?cep=${value}`);

            // Validates if the request returned any errors
            if (response.data.error) {
                // Flags that the desired zip is unavailable
                setErrors({
                    isUnavailable: true,
                })

                // Resets the state
                setZipData({
                    address: "",
                    value:   "",
                })

                // Resets the formatted data to be sent to the main controller
                const data = {
                    id:      "",
                    zip:     "",
                    address: ""
                }

                // Assigns the formatted data to the main controller state
                handleSelectedValue({
                    data
                })

                // Deactivates the full page loader component
                handleCartLoader(false);

                handleErrors(true);

                return;
            }

            // Destructures the address prop from the response data object
            const {address, freight} = response.data;

            // Formats the address to the desired format
            const formattedAddress = `${address.logradouro} - ${address.localidade} - ${address.uf}`;

            // Resets the error state
            setErrors({
                isUnavailable: false,
            })

            // Sets the fetched, formatted data to the zipData state
            setZipData({
                address: formattedAddress,
                value:   freight?.value,
            })

            // The formatted data to be sent to the main controller
            const data = {
                id:      attendanceId,
                zip:     formValues.zip,
                address: formattedAddress
            }

            // Assigns the formatted data to the main controller state
            handleSelectedValue({
                data
            })

            // Clears any errors
            handleErrors(false);

            // Deactivates the full page loader component
            handleCartLoader(false);
        } catch (err) {
            // In case there's any error during the request to the backend
            console.error("Error while fetching zip code:", err);
        }
    }

    // Parses whether the freight is free or not
    useEffect(() => {
        const freightValue = parseInt(zipData.value, 10);

        if (!Number.isInteger(freightValue)) {
            return;
        }

        handleIsFreeFreight(freightValue === 0);
    }, [zipData]);

    // Automatically fetches the user's zip address data when the input field reaches 9 digits
    useEffect(() => {
        if (formValues.zip.length > 8) {
            handleBlur();
        }
    }, [formValues.zip]);

    return {
        formValues,
        zipData,
        errors,
        handleChange,
        handleBlur
    }
}
